
import { useState, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { usePatientsStore } from "@/lib/stores/patients";
import { usePrescriptionsStore } from "@/lib/stores/prescriptions";
import { PatientCard } from "@/components/queue/PatientCard";
import { PatientStatus } from "@/types";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getSmartFilteredPatients } from "@/lib/utils/patientFiltering";
import { useAuthStore } from "@/lib/auth";

const Queue = () => {
  const [activeTab, setActiveTab] = useState<PatientStatus>("waiting");
  
  const { patients, fetchPatients, lastUpdated } = usePatientsStore();
  const fetchPrescriptions = usePrescriptionsStore((state) => state.fetchPrescriptions);
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';
  
  useEffect(() => {
    // Fetch patients and prescriptions when queue loads
    fetchPatients();
    void fetchPrescriptions();
  }, [fetchPatients, fetchPrescriptions]);

  // Force re-render when patient data is updated
  useEffect(() => {
    // This effect will trigger re-render when lastUpdated changes
    // which happens when patient status or doctor assignment changes
  }, [lastUpdated]);
  
  // Get smart filtered patients - simplified for staff
  const filteredPatients = getSmartFilteredPatients(patients, {
    showAll: false,
    daysBack: 2,
    includeActive: true,
    includeFollowUps: isAdmin || isStaff ? false : true, // Disable follow-ups for staff
    includePendingPayments: isAdmin || isStaff ? false : true // Disable pending payments for staff
  });
  
  // Filter by active tab
  const patientsByStatus = filteredPatients.filter(patient => patient.status === activeTab);
  
  // Calculate patient counts for each status (using filtered patients)
  const patientCounts = {
    waiting: filteredPatients.filter(p => p.status === "waiting").length,
    inConsultation: filteredPatients.filter(p => p.status === "in-consultation").length,
    completed: filteredPatients.filter(p => p.status === "completed").length,
    noShow: filteredPatients.filter(p => p.status === "no-show").length
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Patient Queue</h1>
            <p className="text-gray-500 text-sm sm:text-base">
              Showing recent patients (last 2 days + active + follow-ups)
            </p>
          </div>
          <Button asChild className="bg-hospital-primary hover:bg-hospital-primary/90 w-full sm:w-auto">
            <Link to="/registration">
              <PlusCircle className="mr-2 h-4 w-4" />
              New Patient
            </Link>
          </Button>
        </div>
        
        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as PatientStatus)}>
            <TabsList className="grid grid-cols-2 sm:grid-cols-4 mb-6 w-full">
              <TabsTrigger value="waiting" className="data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-700 text-xs sm:text-sm">
                <span className="hidden sm:inline">Waiting</span>
                <span className="sm:hidden">Wait</span> ({patientCounts.waiting})
              </TabsTrigger>
              <TabsTrigger value="in-consultation" className="data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700 text-xs sm:text-sm">
                <span className="hidden sm:inline">In Consultation</span>
                <span className="sm:hidden">Consult</span> ({patientCounts.inConsultation})
              </TabsTrigger>
              <TabsTrigger value="completed" className="data-[state=active]:bg-green-100 data-[state=active]:text-green-700 text-xs sm:text-sm">
                <span className="hidden sm:inline">Completed</span>
                <span className="sm:hidden">Done</span> ({patientCounts.completed})
              </TabsTrigger>
              <TabsTrigger value="no-show" className="data-[state=active]:bg-gray-100 data-[state=active]:text-gray-700 text-xs sm:text-sm">
                <span className="hidden sm:inline">No Show</span>
                <span className="sm:hidden">No Show</span> ({patientCounts.noShow})
              </TabsTrigger>
            </TabsList>
            
            <div className="space-y-3">
              {patientsByStatus.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">No patients in this category</p>
                  <p className="text-sm">
                    Add new patients from the registration page or check the History module for older patients
                  </p>
                </div>
              ) : (
                patientsByStatus.map((patient) => (
                  <PatientCard key={patient.id} patient={patient} />
                ))
              )}
            </div>
          </Tabs>
        </div>
      </div>
    </MainLayout>
  );
};

export default Queue;
